import { useEffect, useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { notify } from '../../helpers/toastify';

import {
  UPLOAD_ACTIVITY_FILE,
  ACTIVITIES_FOR_ONE_MISSION,
  UPSERT_NPS_FOLLOWUP,
  GET_MISSION_FOLLOWUP,
  DELETE_ACTIVITY_SUPPLIER_INVOICE,
  SIGN_BLOOMER_ACTIVITY,
  CANCEL_SIGN_BLOOMER_ACTIVITY,
} from '@bloomays-lib/adapter.api-bloomer';

import { WithErrorBoundary, LoaderSkeleton } from '@bloomays-lib/ui.shared';
import { IActivity, IMission } from '@bloomays-lib/types.shared';
import { activityToActivitySummary } from '../../helpers/CRA';
import { errorLogger, getApolloErrorStatusCode, getAppoloErrorExtensions } from '../../helpers/error';
import { FileActivityObject, ActivitySummary } from '../../types/components';
import TableManagementCRA from '../organisms/TableManagementCRA';
import { useTranslation } from 'react-i18next';

const CRAManagement = () => {
  const { t } = useTranslation(['notify']);
  const { missionRecordId } = useParams<{ missionRecordId: string }>();
  const [uploadActityFile] = useMutation(UPLOAD_ACTIVITY_FILE);
  const [signBloomerActivityMutation] = useMutation(SIGN_BLOOMER_ACTIVITY);
  const [cancelBloomerActivityMutation] = useMutation(CANCEL_SIGN_BLOOMER_ACTIVITY);
  const { error, data, loading, refetch } = useQuery<{ missions: IMission[] }>(ACTIVITIES_FOR_ONE_MISSION, {
    variables: { recordId: missionRecordId },
  });
  const [bloomerFollowUp] = useMutation(UPSERT_NPS_FOLLOWUP);
  const [getNPS] = useLazyQuery(GET_MISSION_FOLLOWUP);

  const [deleteInvoice] = useMutation<{ deleteActivitySupplierInvoice: IActivity }>(DELETE_ACTIVITY_SUPPLIER_INVOICE, {
    refetchQueries: [
      {
        query: ACTIVITIES_FOR_ONE_MISSION,
        variables: { recordId: missionRecordId },
      },
    ],
  });

  const missionLoaded = data && data.missions;
  const [displayNewActivity, setDisplayNewActivity] = useState(false);
  const [newActivityValue, setNewActivityValue] = useState<Date | null>(new Date());
  const [arrayOfCRA, setArrayOfCRA] = useState<ActivitySummary[]>([]);
  const [stateNPS, setStateNPS] = useState({
    loading: false,
  });

  const upsertFollowUp = async (args: {
    selected?: number;
    comment?: string;
    collectDate: Date;
    activityId: string;
  }) => {
    try {
      setStateNPS({ ...stateNPS, loading: true });
      const followUp = await bloomerFollowUp({
        variables: {
          input: {
            score: args.selected,
            comment: args?.comment,
            collectDate: args.collectDate,
            activityId: args.activityId,
            type: 'bloomer',
          },
        },
        refetchQueries: [
          {
            query: GET_MISSION_FOLLOWUP,
            variables: {
              month: args.collectDate,
              type: 'bloomer',
              activityId: args.activityId,
            },
          },
        ],
      });
      setStateNPS({ ...stateNPS, loading: false });
      return followUp;
    } catch (e: any) {
      errorLogger(e, {
        extraInfos: 'Nps bloomer follow up failed',
        state: { recordId: missionRecordId },
      });
      setStateNPS({ ...stateNPS, loading: false });
    }
  };

  const missionToActivitySummaries = (arrData: IMission[]): ActivitySummary[][] => {
    const arrMonthsFilled = arrData.map((mission: IMission) => {
      if (!mission.activity) {
        return [];
      }
      const activities = mission.activity;
      return activities.map((activity: IActivity) => {
        return activityToActivitySummary(activity, mission);
      });
    });
    return arrMonthsFilled;
  };

  useEffect(() => {
    if (error) {
      notify('error', t('randomLKError'), error);
    }
  }, [error, t]);

  useEffect(() => {
    if (missionLoaded) {
      const dataFilled = missionToActivitySummaries(missionLoaded);
      const sortedDataFilled = dataFilled?.[0]?.sort((a, b) => {
        return +b.date - +a.date;
      });
      setArrayOfCRA(sortedDataFilled);
    }
  }, [missionLoaded]);

  const displayRow = () => {
    setDisplayNewActivity(true);
  };

  const cancelBloomerActivity = async (activityId: string, row: string | number | null) => {
    try {
      const cancelInvite = await cancelBloomerActivityMutation({
        variables: {
          activityRecordID: activityId,
        },
        refetchQueries: [
          {
            query: ACTIVITIES_FOR_ONE_MISSION,
            variables: { recordId: missionRecordId },
          },
        ],
        awaitRefetchQueries: true,
      });
      if (cancelInvite.data) {
        notify('success', t('signCancel'));
      }
    } catch (error: any) {
      notify('error', t('signError'), error);
      errorLogger(error, {
        extraInfos: 'Impossible to cancel invite sign CRA',
        state: { activityId: activityId },
      });
    }
  };

  const signBloomerActivity = async (activityId: string, signerFullname: string) => {
    try {
      const signInvite = await signBloomerActivityMutation({
        variables: {
          activityRecordID: activityId,
          signerFullname: signerFullname,
        },
        refetchQueries: [
          {
            query: ACTIVITIES_FOR_ONE_MISSION,
            variables: { recordId: missionRecordId },
          },
        ],
      });
      if (signInvite.data) {
        notify('success', t('signSuccess'));
      }
    } catch (error) {
      notify('error', t('signError'), error, {
        autoClose: false,
        hideProgressBar: true,
      });
    }
  };

  const mission = missionLoaded?.[0];

  const uploadFile =
    (activityFileType: 'Invoice' | 'CRA', extraErrorInfos: any, signerFullname?: string) =>
    async (activityRecordId: string, files: FileActivityObject) => {
      try {
        await uploadActityFile({
          variables: {
            input: {
              activityRecordId: activityRecordId,
              file: files,
              activityFileType: activityFileType,
              signerFullname: signerFullname,
            },
          },
          refetchQueries: [
            {
              query: ACTIVITIES_FOR_ONE_MISSION,
              variables: { recordId: missionRecordId },
            },
          ],
        });
        notify('success', 'Envoyé avec succès ! :)');
      } catch (error: any) {
        const statusCode = getApolloErrorStatusCode(error);
        const apolloErrorExt = getAppoloErrorExtensions(error);
        if (apolloErrorExt?.code === 'SOCIETY_NOT_FOUND') {
          notify(
            'warning',
            'Votre société nest pas définie pour cette mission. Saisir les informations',
            undefined,
            undefined,
            `/society/new/mission/${mission?.recordId}`,
          );
        } else {
          if (statusCode >= 500) {
            notify('error', 'Non envoyé. Souci avec le serveur, reviens plus tard 😓', error);
            errorLogger(error, {
              extraInfos: extraErrorInfos,
            });
          } else {
            notify('warning', error.message);
          }
        }
      }
    };

  if (loading || !mission) {
    return <LoaderSkeleton height={500} width={1200} />;
  }

  return (
    <div>
      <TableManagementCRA
        getNPS={getNPS}
        upsertFollowUp={upsertFollowUp}
        refetchActivities={refetch}
        signBloomerActivity={signBloomerActivity}
        setNewActivityValue={setNewActivityValue}
        cancelBloomerActivity={cancelBloomerActivity}
        displayRow={displayRow}
        arrayOfCRA={arrayOfCRA}
        displayNewActivity={displayNewActivity}
        newActivityValue={newActivityValue}
        error={error}
        uploadCRA={uploadFile('CRA', `Impossible d'envoyer un CRA`)}
        uploadInvoice={uploadFile('Invoice', `Impossible d'envoyer un Invoice`)}
        mission={mission}
        stateNPS={stateNPS}
        deleteInvoice={async (activityRecordID: string) => {
          await deleteInvoice({
            variables: {
              activityRecordID: activityRecordID,
              force: false,
            },
          }).catch((e) => {
            notify('error', 'Impossible de supprimer la facture', e);
          });
        }}
      />
    </div>
  );
};

export default WithErrorBoundary(CRAManagement);
